// Blog draft review — the "blog" channel under the Marketing category.
//
// A read-and-review surface for DRAFT posts destined for the public
// The Hub Ecosystems blog (published by the lead from a different repo).
// Left: list of drafts with a status badge. Right: rendered draft body,
// an Approve button, a Request changes button, and owner notes.
//
// Modeled 1:1 on the docs channel renderer (frontend/src/channels/docs.ts):
// same sidebar/content two-pane shell, same markdown pipeline (renderMarkdown
// from chat.ts), same CSS-variable theming, same channel_kind dispatch. It is
// registered in content-area.ts KIND_RENDERERS as `blog`.

import { get, post } from '../api';
import { state } from '../state';
import { renderMarkdown } from './chat';
import { showToast, errToast } from '../components/toast';

/* ── types (mirror src/handlers/blog.rs) ─────────────────────────── */
interface BlogDraft {
  id: string;
  server_id: string;
  title: string;
  body: string;
  status: string; // 'draft' | 'approved' | 'changes_requested'
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

interface BlogDraftNote {
  id: string;
  draft_id: string;
  author_id: string;
  body: string;
  created_at: string;
}

interface DraftDetail extends BlogDraft {
  notes: BlogDraftNote[];
}

interface BlogDraftVersion {
  id: string;
  draft_id: string;
  version_no: number;
  title: string;
  body: string;
  status: string;
  archived_at: string;
}

let currentDraftId = '';

/* ── cleanup ─────────────────────────────────────────────────────── */
export function cleanupBlog(): void {
  currentDraftId = '';
}

/* ── render ──────────────────────────────────────────────────────── */
export async function renderBlog(container: HTMLElement): Promise<void> {
  // Self-check marker for devtools / diagnostics (matches docs pattern).
  container.dataset.rendererKind = 'blog';

  container.innerHTML = `<div class="blog-wrap blog-wrap-cards">
    <div class="blog-content" id="blog-content">
      <div class="blog-cards-header">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
        <span>Blog Drafts</span>
      </div>
      <div class="blog-cards-grid" id="blog-nav">
        <div class="blog-nav-loading">Loading drafts…</div>
      </div>
    </div>
  </div>`;

  injectStyles();

  const grid = container.querySelector('#blog-nav') as HTMLElement;
  const content = container.querySelector('#blog-content') as HTMLElement;

  const sid = state.currentServerId ?? state.currentServer ?? '';
  if (!sid) {
    grid.innerHTML = '<div class="blog-nav-error">No server selected.</div>';
    return;
  }

  let drafts: BlogDraft[] = [];
  try {
    drafts = await get<BlogDraft[]>(`/api/blog/drafts?server_id=${encodeURIComponent(sid)}`);
  } catch {
    grid.innerHTML = '<div class="blog-nav-error">Failed to load drafts.</div>';
    return;
  }

  const reloadAll = (): void => { void renderBlog(container); };

  const showCardsView = (): void => {
    content.querySelector('.blog-cards-header')?.removeAttribute('style');
    grid.style.display = '';
    const detail = content.querySelector('.blog-detail-view');
    if (detail) detail.remove();
    currentDraftId = '';
  };

  renderCards(grid, drafts, sid, reloadAll);

  grid.addEventListener('click', (e) => {
    const card = (e.target as HTMLElement).closest('.blog-card') as HTMLElement | null;
    if (!card) return;
    const id = card.dataset.id ?? '';
    if (!id) return;
    grid.style.display = 'none';
    content.querySelector('.blog-cards-header')?.setAttribute('style', 'display:none');
    const detailEl = document.createElement('div');
    detailEl.className = 'blog-detail-view';
    content.appendChild(detailEl);
    void loadDraftInto(id, detailEl, showCardsView, drafts);
  });
}

/* ── card grid ───────────────────────────────────────────────────── */
function renderCards(grid: HTMLElement, drafts: BlogDraft[], sid: string, onRefresh: () => void): void {
  const cards = drafts.map(d => {
    const snippet = (d.body || '').replace(/[#*_~`>\[\]]/g, '').slice(0, 120).trim();
    return `<button class="blog-card" data-id="${esc(d.id)}" type="button">
      <div class="blog-card-top">
        <span class="blog-card-title">${esc(d.title || 'Untitled draft')}</span>
        ${statusBadge(d.status)}
      </div>
      <div class="blog-card-snippet">${esc(snippet || 'No content yet.')}${snippet.length >= 120 ? '…' : ''}</div>
      <div class="blog-card-meta">Updated ${fmtDate(d.updated_at)}</div>
    </button>`;
  }).join('');

  grid.innerHTML = cards + `<button class="blog-card blog-card-create" id="blog-create-draft" type="button">
    <div class="blog-card-top">
      <span class="blog-card-title" style="color:var(--ok)">New Draft</span>
    </div>
    <div class="blog-card-snippet" style="color:var(--fg-muted)">Create a new blog post draft</div>
    <div class="blog-card-icon-add">
      <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
    </div>
  </button>`;

  document.getElementById('blog-create-draft')?.addEventListener('click', () => {
    showCreateDraftModal(sid, onRefresh);
  });
}

function showCreateDraftModal(sid: string, onRefresh: () => void): void {
  const existing = document.querySelector('.blog-modal-backdrop');
  if (existing) existing.remove();

  const backdrop = document.createElement('div');
  backdrop.className = 'blog-modal-backdrop';
  backdrop.innerHTML = `<div class="blog-modal">
    <div class="blog-modal-title">New Blog Draft</div>
    <input class="blog-modal-input" id="blog-draft-title" placeholder="Post title" maxlength="200" autocomplete="off" />
    <textarea class="blog-modal-textarea" id="blog-draft-body" placeholder="Start writing (markdown supported)..." rows="6"></textarea>
    <div class="blog-modal-actions">
      <button class="blog-btn blog-btn-changes" id="blog-draft-cancel">Cancel</button>
      <button class="blog-btn blog-btn-approve" id="blog-draft-save">Create Draft</button>
    </div>
  </div>`;
  document.body.appendChild(backdrop);

  backdrop.addEventListener('click', (e) => { if (e.target === backdrop) backdrop.remove(); });
  document.getElementById('blog-draft-cancel')?.addEventListener('click', () => backdrop.remove());
  document.getElementById('blog-draft-save')?.addEventListener('click', async () => {
    const title = (document.getElementById('blog-draft-title') as HTMLInputElement).value.trim();
    const body = (document.getElementById('blog-draft-body') as HTMLTextAreaElement).value.trim();
    if (!title) { showToast('Enter a title', 'err'); return; }
    try {
      await post('/api/blog/drafts/create', { server_id: sid, title, body: body || '' });
      backdrop.remove();
      showToast('Draft created', 'ok');
      onRefresh();
    } catch (err) { errToast(err); }
  });

  (document.getElementById('blog-draft-title') as HTMLInputElement)?.focus();
}

/* ── load + render a single draft into a detail element ──────────── */
async function loadDraftInto(
  id: string,
  detailEl: HTMLElement,
  onBack: () => void,
  drafts: BlogDraft[],
): Promise<void> {
  currentDraftId = id;
  detailEl.innerHTML = '<div class="blog-loading"><div class="blog-spinner"></div>Loading…</div>';

  let detail: DraftDetail;
  try {
    detail = await get<DraftDetail>(`/api/blog/drafts/${encodeURIComponent(id)}`);
  } catch {
    if (currentDraftId !== id) return;
    detailEl.innerHTML = '<div class="blog-error">Failed to load this draft.</div>';
    return;
  }
  if (currentDraftId !== id) return;

  paintDraft(detail, detailEl, onBack, drafts);
}

function paintDraft(detail: DraftDetail, contentEl: HTMLElement, onBack: () => void, drafts: BlogDraft[]): void {
  const bodyHtml = renderMarkdown(detail.body || '');
  const approvedLine = detail.status === 'approved' && detail.approved_at
    ? `<div class="blog-approved-line">Approved${detail.approved_by ? ` by ${esc(personName(detail.approved_by))}` : ''} · ${fmtDate(detail.approved_at)}</div>`
    : '';

  contentEl.innerHTML = `<div class="blog-article">
    <div class="blog-article-head">
      <button class="blog-back-btn" id="blog-back" type="button">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg>
        All drafts
      </button>
      <div class="blog-article-titlerow">
        <h1 class="blog-article-title">${esc(detail.title || 'Untitled draft')}</h1>
        ${statusBadge(detail.status)}
      </div>
      <div class="blog-article-meta">Draft for the public The Hub Ecosystems blog · updated ${fmtDate(detail.updated_at)}</div>
      ${approvedLine}
      <div class="blog-actions">
        <button class="blog-btn blog-btn-approve" id="blog-approve" type="button"${detail.status === 'approved' ? ' disabled' : ''}>
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          ${detail.status === 'approved' ? 'Approved' : 'Approve'}
        </button>
        <button class="blog-btn blog-btn-changes" id="blog-changes" type="button"${detail.status === 'changes_requested' ? ' disabled' : ''}>
          Request changes
        </button>
        <button class="blog-btn blog-btn-edit" id="blog-edit" type="button">Edit</button>
      </div>
    </div>

    <div class="blog-article-body">${bodyHtml}</div>

    <div class="blog-notes">
      <div class="blog-notes-header">Review notes</div>
      <div class="blog-notes-list" id="blog-notes-list">${renderNotes(detail.notes)}</div>
      <div class="blog-note-compose">
        <textarea class="blog-note-input" id="blog-note-input" rows="3"
          placeholder="Add a note for the lead — what to change, tone, corrections…"></textarea>
        <button class="blog-btn blog-btn-note" id="blog-note-add" type="button">Add note</button>
      </div>
    </div>

    <div class="blog-history">
      <div class="blog-history-header">Version history <span id="blog-history-count"></span></div>
      <div class="blog-history-list" id="blog-history-list"><div class="blog-history-empty">Loading…</div></div>
    </div>
  </div>`;

  contentEl.scrollTop = 0;

  contentEl.querySelector('#blog-back')?.addEventListener('click', onBack);

  const approveBtn = contentEl.querySelector('#blog-approve') as HTMLButtonElement | null;
  approveBtn?.addEventListener('click', () => void onApprove(detail.id, contentEl, onBack, drafts));

  const changesBtn = contentEl.querySelector('#blog-changes') as HTMLButtonElement | null;
  changesBtn?.addEventListener('click', () => void onRequestChanges(detail.id, contentEl, onBack, drafts));

  const addNoteBtn = contentEl.querySelector('#blog-note-add') as HTMLButtonElement | null;
  addNoteBtn?.addEventListener('click', () => void onAddNote(detail.id, contentEl));

  const noteInput = contentEl.querySelector('#blog-note-input') as HTMLTextAreaElement | null;
  noteInput?.addEventListener('focus', () => {
    setTimeout(() => noteInput.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
  });

  const editBtn = contentEl.querySelector('#blog-edit') as HTMLButtonElement | null;
  editBtn?.addEventListener('click', () => showEditDraftModal(detail, contentEl, onBack, drafts));

  void loadHistory(detail.id, contentEl, onBack, drafts);
}

/* ── actions ─────────────────────────────────────────────────────── */
async function onApprove(id: string, contentEl: HTMLElement, onBack: () => void, drafts: BlogDraft[]): Promise<void> {
  try {
    await post(`/api/blog/drafts/${encodeURIComponent(id)}/approve`, {
      person_id: state.ownerId || 'owner',
    });
    showToast('Draft approved — the lead will publish it.', 'ok');
    await refresh(id, contentEl, onBack, drafts);
  } catch (err) {
    showToast(`Approve failed: ${(err as Error).message}`, 'err');
  }
}

async function onRequestChanges(id: string, contentEl: HTMLElement, onBack: () => void, drafts: BlogDraft[]): Promise<void> {
  try {
    await post(`/api/blog/drafts/${encodeURIComponent(id)}/request-changes`, {
      person_id: state.ownerId || 'owner',
    });
    showToast('Marked as changes requested.', 'ok');
    await refresh(id, contentEl, onBack, drafts);
  } catch (err) {
    showToast(`Request changes failed: ${(err as Error).message}`, 'err');
  }
}

async function onAddNote(id: string, contentEl: HTMLElement): Promise<void> {
  const input = contentEl.querySelector('#blog-note-input') as HTMLTextAreaElement | null;
  const btn = contentEl.querySelector('#blog-note-add') as HTMLButtonElement | null;
  const text = (input?.value ?? '').trim();
  if (!text) {
    showToast('Write a note first.', 'err');
    return;
  }
  if (btn) btn.disabled = true;
  try {
    const note = await post<BlogDraftNote>(`/api/blog/drafts/${encodeURIComponent(id)}/notes`, {
      body: text,
      author_id: state.ownerId || 'owner',
    });
    // Append the new note without a full reload.
    const list = contentEl.querySelector('#blog-notes-list') as HTMLElement | null;
    if (list) {
      const empty = list.querySelector('.blog-notes-empty');
      if (empty) list.innerHTML = '';
      list.insertAdjacentHTML('beforeend', renderNote(note));
    }
    if (input) input.value = '';
    showToast('Note added.', 'ok');
  } catch (err) {
    showToast(`Add note failed: ${(err as Error).message}`, 'err');
  } finally {
    if (btn) btn.disabled = false;
  }
}

async function refresh(id: string, contentEl: HTMLElement, onBack: () => void, drafts: BlogDraft[]): Promise<void> {
  try {
    const detail = await get<DraftDetail>(`/api/blog/drafts/${encodeURIComponent(id)}`);
    if (currentDraftId !== id) return;
    paintDraft(detail, contentEl, onBack, drafts);
  } catch {
    /* leave the current view as-is on refresh failure */
  }
}

/* ── edit a draft (archives the prior version server-side) ───────── */
function showEditDraftModal(draft: BlogDraft, contentEl: HTMLElement, onBack: () => void, drafts: BlogDraft[]): void {
  document.querySelector('.blog-modal-backdrop')?.remove();

  const backdrop = document.createElement('div');
  backdrop.className = 'blog-modal-backdrop';
  const modal = document.createElement('div');
  modal.className = 'blog-modal';
  modal.innerHTML = `
    <div class="blog-modal-title">Edit draft</div>
    <input class="blog-modal-input" id="blog-edit-title" placeholder="Post title" maxlength="200" autocomplete="off" />
    <textarea class="blog-modal-textarea" id="blog-edit-body" placeholder="Markdown supported…" rows="10"></textarea>
    <div class="blog-modal-note">Saving archives the current version first — nothing is lost. Editing resets the draft to “draft” for re-review.</div>
    <div class="blog-modal-actions">
      <button class="blog-btn blog-btn-changes" id="blog-edit-cancel" type="button">Cancel</button>
      <button class="blog-btn blog-btn-approve" id="blog-edit-save" type="button">Save changes</button>
    </div>`;
  backdrop.appendChild(modal);
  document.body.appendChild(backdrop);
  (modal.querySelector('#blog-edit-title') as HTMLInputElement).value = draft.title || '';
  (modal.querySelector('#blog-edit-body') as HTMLTextAreaElement).value = draft.body || '';

  const close = (): void => backdrop.remove();
  backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(); });
  modal.querySelector('#blog-edit-cancel')?.addEventListener('click', close);
  modal.querySelector('#blog-edit-save')?.addEventListener('click', async () => {
    const title = (modal.querySelector('#blog-edit-title') as HTMLInputElement).value.trim();
    const body = (modal.querySelector('#blog-edit-body') as HTMLTextAreaElement).value;
    if (!title) { showToast('Enter a title', 'err'); return; }
    try {
      await post(`/api/blog/drafts/${encodeURIComponent(draft.id)}/update`, { title, body });
      close();
      showToast('Draft updated — prior version archived.', 'ok');
      await refresh(draft.id, contentEl, onBack, drafts);
    } catch (err) { errToast(err); }
  });
  (modal.querySelector('#blog-edit-title') as HTMLInputElement)?.focus();
}

/* ── version history ─────────────────────────────────────────────── */
async function loadHistory(id: string, contentEl: HTMLElement, onBack: () => void, drafts: BlogDraft[]): Promise<void> {
  const listEl = contentEl.querySelector('#blog-history-list') as HTMLElement | null;
  if (!listEl) return;

  let versions: BlogDraftVersion[] = [];
  try {
    versions = await get<BlogDraftVersion[]>(`/api/blog/drafts/${encodeURIComponent(id)}/versions`);
  } catch {
    if (currentDraftId === id) listEl.innerHTML = '<div class="blog-history-empty">Could not load history.</div>';
    return;
  }
  if (currentDraftId !== id) return;

  const countEl = contentEl.querySelector('#blog-history-count') as HTMLElement | null;
  if (countEl) countEl.textContent = versions.length ? `(${versions.length})` : '';
  if (!versions.length) {
    listEl.innerHTML = '<div class="blog-history-empty">No earlier versions yet. Editing a draft archives the version before it.</div>';
    return;
  }

  listEl.innerHTML = versions.map(v => `
    <div class="blog-version">
      <div class="blog-version-meta">
        <span class="blog-version-no">v${v.version_no}</span>
        <span class="blog-version-title">${esc(v.title || 'Untitled')}</span>
        <span class="blog-version-time">${fmtDate(v.archived_at)}</span>
      </div>
      <div class="blog-version-actions">
        <button class="blog-btn blog-btn-edit blog-version-view" type="button" data-vid="${esc(v.id)}">View</button>
        <button class="blog-btn blog-btn-edit blog-version-restore" type="button" data-vid="${esc(v.id)}">Restore</button>
      </div>
    </div>`).join('');

  listEl.querySelectorAll<HTMLButtonElement>('.blog-version-view').forEach(btn =>
    btn.addEventListener('click', () => {
      const v = versions.find(x => x.id === btn.dataset.vid);
      if (v) viewVersion(v);
    }));
  listEl.querySelectorAll<HTMLButtonElement>('.blog-version-restore').forEach(btn =>
    btn.addEventListener('click', () => void onRestore(id, btn.dataset.vid ?? '', contentEl, onBack, drafts)));
}

function viewVersion(v: BlogDraftVersion): void {
  document.querySelector('.blog-modal-backdrop')?.remove();
  const backdrop = document.createElement('div');
  backdrop.className = 'blog-modal-backdrop';
  const modal = document.createElement('div');
  modal.className = 'blog-modal blog-version-modal';
  modal.innerHTML = `
    <div class="blog-modal-title">v${v.version_no} · ${esc(v.title || 'Untitled')}</div>
    <div class="blog-version-viewbody">${renderMarkdown(v.body || '')}</div>
    <div class="blog-modal-actions">
      <button class="blog-btn blog-btn-approve" id="blog-version-close" type="button">Close</button>
    </div>`;
  backdrop.appendChild(modal);
  document.body.appendChild(backdrop);
  const close = (): void => backdrop.remove();
  backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(); });
  modal.querySelector('#blog-version-close')?.addEventListener('click', close);
}

async function onRestore(id: string, vid: string, contentEl: HTMLElement, onBack: () => void, drafts: BlogDraft[]): Promise<void> {
  if (!vid) return;
  try {
    await post(`/api/blog/drafts/${encodeURIComponent(id)}/versions/${encodeURIComponent(vid)}/restore`, {});
    showToast('Version restored — the replaced content was archived too.', 'ok');
    await refresh(id, contentEl, onBack, drafts);
  } catch (err) {
    showToast(`Restore failed: ${(err as Error).message}`, 'err');
  }
}

/* ── notes rendering ─────────────────────────────────────────────── */
function renderNotes(notes: BlogDraftNote[]): string {
  if (!notes || notes.length === 0) {
    return '<div class="blog-notes-empty">No notes yet. Add the first one below.</div>';
  }
  return notes.map(renderNote).join('');
}

function renderNote(n: BlogDraftNote): string {
  return `<div class="blog-note">
    <div class="blog-note-meta">
      <span class="blog-note-author">${esc(personName(n.author_id) || 'Owner')}</span>
      <span class="blog-note-time">${fmtDate(n.created_at)}</span>
    </div>
    <div class="blog-note-body">${esc(n.body).replace(/\n/g, '<br>')}</div>
  </div>`;
}

/* ── status badge ────────────────────────────────────────────────── */
function statusBadge(status: string): string {
  const map: Record<string, { label: string; cls: string }> = {
    draft: { label: 'Draft', cls: 'blog-badge-draft' },
    approved: { label: 'Approved', cls: 'blog-badge-approved' },
    changes_requested: { label: 'Changes requested', cls: 'blog-badge-changes' },
  };
  const s = map[status] ?? { label: status || 'Draft', cls: 'blog-badge-draft' };
  return `<span class="blog-badge ${s.cls}">${esc(s.label)}</span>`;
}

/* ── helpers ─────────────────────────────────────────────────────── */
function personName(id: string): string {
  if (!id) return '';
  const p = state.people.find(x => x.id === id);
  return p?.name ?? (id === (state.ownerId || '') ? 'You' : id);
}

function fmtDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-AU', {
    timeZone: 'Australia/Sydney',
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/* ── styles ──────────────────────────────────────────────────────── */
let stylesInjected = false;

function injectStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;

  const style = document.createElement('style');
  style.textContent = `
    .blog-wrap {
      display: flex;
      flex-direction: column;
      height: 100%;
      background: var(--bg-primary);
      overflow-y: auto;
      min-height: 0;
    }

    /* ── Cards header ── */
    .blog-cards-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 20px 24px 8px;
      font-size: 14px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: .02em;
      color: var(--fg-muted);
    }
    .blog-cards-header svg { opacity: .7; }

    /* ── Card grid ── */
    .blog-cards-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
      gap: 12px;
      padding: 12px 24px 32px;
    }
    .blog-nav-loading, .blog-nav-empty, .blog-nav-error {
      padding: 12px; font-size: 13px; color: var(--fg-muted);
    }
    .blog-nav-error { color: var(--fg-danger, #e54d4d); }
    .blog-card {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 16px;
      border: 1px solid var(--border-subtle, rgba(255,255,255,.08));
      background: var(--bg-secondary);
      border-radius: 8px;
      cursor: pointer;
      text-align: left;
      transition: border-color .15s, background .15s;
      width: 100%;
    }
    .blog-card:hover {
      border-color: var(--accent);
      background: var(--bg-hover, rgba(255,255,255,.04));
    }
    .blog-card-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }
    .blog-card-title {
      font-size: 15px;
      font-weight: 600;
      color: var(--fg-primary, var(--text-primary));
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
    }
    .blog-card-snippet {
      font-size: 13px;
      line-height: 1.5;
      color: var(--fg-muted);
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .blog-card-meta {
      font-size: 11px;
      color: var(--fg-muted);
      opacity: .7;
    }
    @media (max-width: 768px) {
      .blog-cards-grid { grid-template-columns: 1fr; padding: 12px 12px 24px; gap: 10px; }
      .blog-cards-header { padding: 12px 12px 4px; font-size: 13px; }
      .blog-card { padding: 14px; }
      .blog-card-title { font-size: 14px; }
      .blog-article { padding: 16px 14px 40px; }
      .blog-article-title { font-size: 18px; }
      .blog-article-body { font-size: 13px; line-height: 1.6; }
      .blog-actions { flex-direction: column; }
      .blog-actions .blog-btn { width: 100%; justify-content: center; }
      .blog-notes { margin-top: 24px; padding-top: 16px; }
      .blog-back-btn { padding: 10px 0; min-height: 44px; font-size: 14px; }
    }

    /* ── Back button ── */
    .blog-back-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: transparent;
      border: none;
      color: var(--fg-muted);
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      padding: 6px 0;
      margin-bottom: 12px;
      transition: color .15s;
    }
    .blog-back-btn:hover { color: var(--fg-primary, var(--text-primary)); }

    /* ── Content ── */
    .blog-content {
      flex: 1;
      overflow-y: auto;
      min-height: 0;
      padding: 0;
    }
    .blog-detail-view {
      overflow-y: auto;
      min-height: 0;
      flex: 1;
    }
    .blog-empty, .blog-loading, .blog-error {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      color: var(--fg-muted);
      font-size: 14px;
      gap: 8px;
    }
    .blog-error { color: var(--fg-danger, #e54d4d); }
    .blog-spinner {
      width: 16px; height: 16px;
      border: 2px solid var(--fg-muted);
      border-top-color: transparent;
      border-radius: 50%;
      animation: blog-spin .6s linear infinite;
    }
    @keyframes blog-spin { to { transform: rotate(360deg); } }

    /* ── Article ── */
    .blog-article {
      max-width: 820px;
      margin: 0 auto;
      padding: 32px 40px 64px;
    }
    .blog-article-head {
      padding-bottom: 16px;
      margin-bottom: 20px;
      border-bottom: 1px solid var(--border-subtle, var(--border-color, rgba(255,255,255,.06)));
    }
    .blog-article-titlerow {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
    }
    .blog-article-title {
      font-size: 24px;
      font-weight: 700;
      color: var(--fg-primary, var(--text-primary));
      margin: 0;
    }
    .blog-article-meta {
      font-size: 12px;
      color: var(--fg-muted);
      margin-top: 8px;
    }
    .blog-approved-line {
      font-size: 12px;
      color: var(--fg-success, #3ba55d);
      margin-top: 4px;
    }
    .blog-actions {
      display: flex;
      gap: 8px;
      margin-top: 16px;
      flex-wrap: wrap;
    }
    .blog-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      border-radius: 4px;
      border: 1px solid transparent;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      color: inherit;
      transition: background .15s, opacity .15s;
    }
    .blog-btn:disabled { opacity: .55; cursor: default; }
    .blog-btn-approve {
      background: var(--accent-success, #3ba55d);
      color: #fff;
    }
    .blog-btn-approve:not(:disabled):hover { background: var(--accent-success-hover, #2d9150); }
    .blog-btn-changes {
      background: transparent;
      border-color: var(--border-subtle, rgba(255,255,255,.12));
      color: var(--fg-secondary, var(--fg-muted));
    }
    .blog-btn-changes:not(:disabled):hover {
      background: var(--bg-hover, rgba(255,255,255,.04));
      color: var(--fg-primary, var(--text-primary));
    }

    /* ── Status badge ── */
    .blog-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 10px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: .02em;
      white-space: nowrap;
      flex-shrink: 0;
    }
    .blog-badge-draft {
      background: var(--bg-tertiary, rgba(255,255,255,.08));
      color: var(--fg-muted);
    }
    .blog-badge-approved {
      background: rgba(59, 165, 93, .18);
      color: var(--accent-success, #3ba55d);
    }
    .blog-badge-changes {
      background: rgba(229, 155, 45, .18);
      color: var(--accent-warning, #e59b2d);
    }

    /* ── Body (markdown, mirrors docs styling) ── */
    .blog-article-body {
      font-size: 14px;
      line-height: 1.65;
      color: var(--fg-secondary, var(--fg-muted));
    }
    .blog-article-body .msg-h1 { font-size: 20px; margin: 28px 0 12px; color: var(--fg-primary, var(--text-primary)); }
    .blog-article-body .msg-h2 { font-size: 16px; margin: 24px 0 8px; color: var(--fg-primary, var(--text-primary)); }
    .blog-article-body .msg-h3 { font-size: 14px; margin: 20px 0 6px; color: var(--fg-primary, var(--text-primary)); font-weight: 600; }
    .blog-article-body .msg-hr { border: none; border-top: 1px solid var(--border-subtle, rgba(255,255,255,.06)); margin: 24px 0; }
    .blog-article-body .msg-blockquote {
      border-left: 3px solid var(--accent, #5865f2);
      padding: 4px 12px;
      margin: 8px 0;
      color: var(--fg-muted);
    }
    .blog-article-body strong { color: var(--fg-primary, var(--text-primary)); }
    .blog-article-body .msg-bullet { padding-left: 16px; position: relative; margin: 2px 0; }
    .blog-article-body .msg-bullet::before { content: '\\2022'; position: absolute; left: 4px; color: var(--fg-muted); }
    .blog-article-body .msg-bullet-ordered::before { content: none; }

    /* ── Notes ── */
    .blog-notes {
      margin-top: 40px;
      padding-top: 24px;
      border-top: 1px solid var(--border-subtle, var(--border-color, rgba(255,255,255,.06)));
    }
    .blog-notes-header {
      font-size: 13px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: .02em;
      color: var(--fg-muted);
      margin-bottom: 12px;
    }
    .blog-notes-empty {
      font-size: 13px;
      color: var(--fg-muted);
      padding: 8px 0 16px;
    }
    .blog-note {
      background: var(--bg-secondary);
      border: 1px solid var(--border-subtle, rgba(255,255,255,.06));
      border-radius: 6px;
      padding: 10px 12px;
      margin-bottom: 8px;
    }
    .blog-note-meta {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 4px;
    }
    .blog-note-author {
      font-size: 13px;
      font-weight: 600;
      color: var(--fg-primary, var(--text-primary));
    }
    .blog-note-time { font-size: 11px; color: var(--fg-muted); }
    .blog-note-body {
      font-size: 14px;
      line-height: 1.5;
      color: var(--fg-secondary, var(--fg-muted));
      white-space: normal;
    }
    .blog-note-compose {
      margin-top: 12px;
      display: flex;
      flex-direction: column;
      gap: 8px;
      align-items: flex-start;
    }
    .blog-note-input {
      width: 100%;
      resize: vertical;
      background: var(--bg-input, var(--bg-tertiary, rgba(0,0,0,.2)));
      border: 1px solid var(--border-subtle, rgba(255,255,255,.1));
      border-radius: 6px;
      padding: 10px 12px;
      color: var(--fg-primary, var(--text-primary));
      font-size: 14px;
      font-family: inherit;
      line-height: 1.5;
    }
    .blog-note-input:focus {
      outline: none;
      border-color: var(--accent, #5865f2);
    }
    .blog-btn-note {
      background: var(--accent, #5865f2);
      color: #fff;
    }
    .blog-btn-note:not(:disabled):hover { background: var(--accent-hover, #4752c4); }

    /* ── Create card ── */
    .blog-card-create { border-style: dashed; align-items: center; }
    .blog-card-icon-add { color: var(--ok); margin-top: 4px; }

    /* ── Create modal ── */
    .blog-modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.6); z-index:var(--z-modal,300); display:flex; align-items:center; justify-content:center; padding:16px; }
    .blog-modal { background:var(--bg-floating,var(--bg)); border-radius:12px; padding:24px; width:100%; max-width:520px; display:flex; flex-direction:column; gap:16px; }
    .blog-modal-title { font-size:18px; font-weight:700; color:var(--fg); }
    .blog-modal-input,.blog-modal-textarea { width:100%; padding:10px 12px; background:var(--bg-input,var(--bg-tertiary)); border:1px solid var(--border); border-radius:6px; color:var(--fg); font:inherit; font-size:14px; }
    .blog-modal-input:focus,.blog-modal-textarea:focus { outline:none; border-color:var(--accent); }
    .blog-modal-textarea { resize:vertical; min-height:120px; line-height:1.5; }
    .blog-modal-actions { display:flex; gap:8px; justify-content:flex-end; }

    /* ── Mobile ── */
    @media (max-width: 768px) {
      .blog-article { padding: 16px 16px 40px; }
      .blog-article-title { font-size: 20px; }
      .blog-back-btn { min-height: 44px; font-size: 14px; }
      .blog-btn { min-height: 44px; padding: 10px 16px; }
      .blog-note-input { font-size: 16px; min-height: 44px; }
      .blog-note-compose { align-items: stretch; }
      .blog-btn-note { width: 100%; justify-content: center; }
      .blog-actions { flex-direction: column; }
      .blog-actions .blog-btn { width: 100%; justify-content: center; }
      .blog-notes { margin-top: 24px; padding-top: 16px; padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px)); }
      .blog-modal-backdrop { align-items: flex-end; }
      .blog-modal { border-radius: 12px 12px 0 0; padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px)); max-width: 100%; }
      .blog-modal-input,.blog-modal-textarea { font-size: 16px; min-height: 44px; }
      .blog-cards-grid { grid-template-columns: 1fr; padding: 12px; gap: 10px; }
    }
    @media (max-width: 420px) {
      .blog-article { padding: 12px 12px 32px; }
      .blog-article-title { font-size: 18px; }
      .blog-note { padding: 8px 10px; }
    }

    /* ── Edit button + version history ── */
    .blog-btn-edit {
      background: transparent;
      border-color: var(--border-subtle, rgba(255,255,255,.12));
      color: var(--fg-secondary, var(--fg-muted));
    }
    .blog-btn-edit:not(:disabled):hover {
      background: var(--bg-hover, rgba(255,255,255,.04));
      color: var(--fg-primary, var(--text-primary));
    }
    .blog-modal-note { font-size: 12px; color: var(--fg-muted); line-height: 1.4; }
    .blog-history {
      max-width: 820px;
      margin: 0 auto;
      padding: 0 40px 48px;
    }
    .blog-history-header {
      font-size: 13px; font-weight: 600; text-transform: uppercase;
      letter-spacing: .02em; color: var(--fg-muted); margin-bottom: 12px;
    }
    .blog-history-empty { font-size: 13px; color: var(--fg-muted); padding: 4px 0; }
    .blog-version {
      display: flex; align-items: center; justify-content: space-between;
      gap: 12px; padding: 10px 12px; margin-bottom: 8px;
      border: 1px solid var(--border-subtle, rgba(255,255,255,.06));
      border-radius: 6px; background: var(--bg-secondary); flex-wrap: wrap;
    }
    .blog-version-meta { display: flex; align-items: baseline; gap: 8px; min-width: 0; flex: 1; }
    .blog-version-no { font-size: 12px; font-weight: 700; color: var(--accent); flex-shrink: 0; }
    .blog-version-title {
      font-size: 13px; color: var(--fg-primary, var(--text-primary));
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .blog-version-time { font-size: 11px; color: var(--fg-muted); flex-shrink: 0; }
    .blog-version-actions { display: flex; gap: 6px; flex-shrink: 0; }
    .blog-version-actions .blog-btn { padding: 4px 10px; font-size: 12px; }
    .blog-version-modal { max-width: 720px; max-height: 82vh; overflow-y: auto; }
    .blog-version-viewbody { font-size: 14px; line-height: 1.6; color: var(--fg-secondary, var(--fg-muted)); }
    @media (max-width: 768px) {
      .blog-history { padding: 0 16px 40px; }
      .blog-version-actions .blog-btn { min-height: 36px; }
    }
  `;
  document.head.appendChild(style);
}
